import { ApiError } from './apiService';

/**
 * HTTP Status Code Constants
 */
export const HTTP_STATUS = {
  // Success
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,

  // Client Errors
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,

  // Server Errors
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
};

/**
 * Error Code Constants
 */
export const ERROR_CODES = {
  // Authentication Errors
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  REFRESH_TOKEN_EXPIRED: 'REFRESH_TOKEN_EXPIRED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  UNAUTHORIZED_ACCESS: 'UNAUTHORIZED_ACCESS',

  // Validation Errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  INVALID_FORMAT: 'INVALID_FORMAT',
  INVALID_EMAIL: 'INVALID_EMAIL',
  INVALID_PASSWORD: 'INVALID_PASSWORD',

  // Network Errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  CONNECTION_FAILED: 'CONNECTION_FAILED',

  // Server Errors
  SERVER_ERROR: 'SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  MAINTENANCE_MODE: 'MAINTENANCE_MODE',

  // Application Errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  PARSE_ERROR: 'PARSE_ERROR',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
};

/**
 * User-friendly error messages
 */
export const ERROR_MESSAGES = {
  [ERROR_CODES.INVALID_CREDENTIALS]:
    'Invalid email or password. Please check your credentials and try again.',
  [ERROR_CODES.TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',
  [ERROR_CODES.TOKEN_INVALID]:
    'Invalid authentication token. Please log in again.',
  [ERROR_CODES.REFRESH_TOKEN_EXPIRED]:
    'Your session has expired. Please log in again.',
  [ERROR_CODES.SESSION_EXPIRED]:
    'Your session has expired. Please log in again.',
  [ERROR_CODES.UNAUTHORIZED_ACCESS]:
    'You do not have permission to access this resource.',

  [ERROR_CODES.VALIDATION_ERROR]: 'Please check your input and try again.',
  [ERROR_CODES.MISSING_REQUIRED_FIELD]: 'Please fill in all required fields.',
  [ERROR_CODES.INVALID_FORMAT]: 'Please check the format of your input.',
  [ERROR_CODES.INVALID_EMAIL]: 'Please enter a valid email address.',
  [ERROR_CODES.INVALID_PASSWORD]: 'Password does not meet the requirements.',

  [ERROR_CODES.NETWORK_ERROR]:
    'Network error. Please check your connection and try again.',
  [ERROR_CODES.TIMEOUT]: 'Request timed out. Please try again.',
  [ERROR_CODES.CONNECTION_FAILED]:
    'Unable to connect to the server. Please try again later.',

  [ERROR_CODES.SERVER_ERROR]: 'Server error. Please try again later.',
  [ERROR_CODES.SERVICE_UNAVAILABLE]:
    'Service is temporarily unavailable. Please try again later.',
  [ERROR_CODES.MAINTENANCE_MODE]:
    'System is under maintenance. Please try again later.',

  [ERROR_CODES.UNKNOWN_ERROR]:
    'An unexpected error occurred. Please try again.',
  [ERROR_CODES.PARSE_ERROR]: 'Error processing response. Please try again.',
  [ERROR_CODES.INVALID_RESPONSE]:
    'Invalid response from server. Please try again.',
};

/**
 * Error Handler Class
 */
class ErrorHandler {
  /**
   * Handle API errors and return user-friendly messages
   * @param {Error|ApiError} error - Error to handle
   * @returns {Object} Processed error object
   */
  static handleError(error) {
    console.error('Error occurred:', error);

    if (error instanceof ApiError) {
      return this.handleApiError(error);
    }

    // Handle generic JavaScript errors
    return this.handleGenericError(error);
  }

  /**
   * Handle API-specific errors
   * @param {ApiError} apiError - API error to handle
   * @returns {Object} Processed error object
   */
  static handleApiError(apiError) {
    const errorData = {
      message: apiError.message,
      code: apiError.code,
      status: apiError.status,
      timestamp: apiError.timestamp,
      isRetryable: false,
      userMessage: '',
      severity: 'error',
    };

    // Determine user-friendly message and error properties
    switch (apiError.status) {
      case HTTP_STATUS.UNAUTHORIZED:
        errorData.userMessage = this.getAuthErrorMessage(apiError);
        errorData.code = apiError.code || ERROR_CODES.TOKEN_EXPIRED;
        errorData.severity = 'warning';
        break;

      case HTTP_STATUS.FORBIDDEN:
        errorData.userMessage = ERROR_MESSAGES[ERROR_CODES.UNAUTHORIZED_ACCESS];
        errorData.code = ERROR_CODES.UNAUTHORIZED_ACCESS;
        errorData.severity = 'warning';
        break;

      case HTTP_STATUS.NOT_FOUND:
        errorData.userMessage = 'The requested resource was not found.';
        errorData.code = 'NOT_FOUND';
        break;

      case HTTP_STATUS.BAD_REQUEST:
      case HTTP_STATUS.UNPROCESSABLE_ENTITY:
        errorData.userMessage = this.getValidationErrorMessage(apiError);
        errorData.code = apiError.code || ERROR_CODES.VALIDATION_ERROR;
        break;

      case HTTP_STATUS.TOO_MANY_REQUESTS:
        errorData.userMessage =
          'Too many requests. Please wait a moment and try again.';
        errorData.code = 'RATE_LIMITED';
        errorData.isRetryable = true;
        break;

      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      case HTTP_STATUS.BAD_GATEWAY:
      case HTTP_STATUS.SERVICE_UNAVAILABLE:
      case HTTP_STATUS.GATEWAY_TIMEOUT:
        errorData.userMessage = ERROR_MESSAGES[ERROR_CODES.SERVER_ERROR];
        errorData.code = ERROR_CODES.SERVER_ERROR;
        errorData.isRetryable = true;
        break;

      case 0: // Network error
        errorData.userMessage = ERROR_MESSAGES[ERROR_CODES.NETWORK_ERROR];
        errorData.code = ERROR_CODES.NETWORK_ERROR;
        errorData.isRetryable = true;
        break;

      default:
        errorData.userMessage = this.getGenericErrorMessage(apiError);
        errorData.code = apiError.code || ERROR_CODES.UNKNOWN_ERROR;
        errorData.isRetryable = apiError.status >= 500;
    }

    return errorData;
  }

  /**
   * Handle generic JavaScript errors
   * @param {Error} error - Generic error to handle
   * @returns {Object} Processed error object
   */
  static handleGenericError(error) {
    return {
      message: error.message || 'Unknown error occurred',
      code: ERROR_CODES.UNKNOWN_ERROR,
      status: 0,
      timestamp: new Date().toISOString(),
      isRetryable: false,
      userMessage: ERROR_MESSAGES[ERROR_CODES.UNKNOWN_ERROR],
      severity: 'error',
    };
  }

  /**
   * Get authentication error message
   * @param {ApiError} apiError - API error
   * @returns {string} User-friendly message
   */
  static getAuthErrorMessage(apiError) {
    if (apiError.code) {
      switch (apiError.code) {
        case 'INVALID_CREDENTIALS':
        case 'LOGIN_FAILED':
          return ERROR_MESSAGES[ERROR_CODES.INVALID_CREDENTIALS];
        case 'TOKEN_EXPIRED':
        case 'REFRESH_FAILED':
          return ERROR_MESSAGES[ERROR_CODES.TOKEN_EXPIRED];
        case 'TOKEN_INVALID':
          return ERROR_MESSAGES[ERROR_CODES.TOKEN_INVALID];
        default:
          return ERROR_MESSAGES[ERROR_CODES.TOKEN_EXPIRED];
      }
    }

    return ERROR_MESSAGES[ERROR_CODES.TOKEN_EXPIRED];
  }

  /**
   * Get validation error message
   * @param {ApiError} apiError - API error
   * @returns {string} User-friendly message
   */
  static getValidationErrorMessage(apiError) {
    // Check if API provides specific validation message
    if (apiError.data && apiError.data.errors) {
      const errors = apiError.data.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        return errors[0].message || errors[0];
      }
    }

    // Check if API provides a specific message
    if (apiError.message && apiError.message !== 'API request failed') {
      return apiError.message;
    }

    return ERROR_MESSAGES[ERROR_CODES.VALIDATION_ERROR];
  }

  /**
   * Get generic error message
   * @param {ApiError} apiError - API error
   * @returns {string} User-friendly message
   */
  static getGenericErrorMessage(apiError) {
    if (apiError.message && apiError.message !== 'API request failed') {
      return apiError.message;
    }

    return ERROR_MESSAGES[ERROR_CODES.UNKNOWN_ERROR];
  }

  /**
   * Create error notification object
   * @param {Error|ApiError} error - Error to create notification for
   * @returns {Object} Notification object
   */
  static createErrorNotification(error) {
    const processedError = this.handleError(error);

    return {
      type: 'error',
      title: 'Error',
      message: processedError.userMessage,
      duration: processedError.severity === 'error' ? 8000 : 5000,
      actions: processedError.isRetryable
        ? [
            {
              label: 'Retry',
              action: 'retry',
            },
          ]
        : [],
    };
  }

  /**
   * Log error for debugging/monitoring
   * @param {Error|ApiError} error - Error to log
   * @param {Object} context - Additional context
   */
  static logError(error, context = {}) {
    const errorData = this.handleError(error);
    const logData = {
      ...errorData,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    // In production, send to error monitoring service
    if (process.env.NODE_ENV === 'production') {
      // Example: Send to error monitoring service
      // Sentry.captureException(error, { extra: logData });
    }

    console.error('Detailed Error Log:', logData);
  }

  /**
   * Check if error should trigger authentication redirect
   * @param {Error|ApiError} error - Error to check
   * @returns {boolean} True if should redirect to login
   */
  static shouldRedirectToLogin(error) {
    if (error instanceof ApiError) {
      return (
        error.status === HTTP_STATUS.UNAUTHORIZED &&
        (error.code === ERROR_CODES.TOKEN_EXPIRED ||
          error.code === ERROR_CODES.TOKEN_INVALID ||
          error.code === ERROR_CODES.REFRESH_TOKEN_EXPIRED)
      );
    }
    return false;
  }

  /**
   * Check if error is retryable
   * @param {Error|ApiError} error - Error to check
   * @returns {boolean} True if error is retryable
   */
  static isRetryable(error) {
    const processedError = this.handleError(error);
    return processedError.isRetryable;
  }

  /**
   * Get retry delay for retryable errors
   * @param {number} attemptNumber - Current attempt number
   * @returns {number} Delay in milliseconds
   */
  static getRetryDelay(attemptNumber) {
    // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
    const baseDelay = 1000;
    const maxDelay = 30000;
    const delay = baseDelay * Math.pow(2, attemptNumber - 1);
    return Math.min(delay, maxDelay);
  }
}

/**
 * Create error boundary component
 * Note: Import React in the component that uses this helper
 */
export const createErrorBoundary =
  React => (WrappedComponent, errorFallback) => {
    return class extends React.Component {
      constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
      }

      static getDerivedStateFromError(error) {
        return { hasError: true, error };
      }

      componentDidCatch(error, errorInfo) {
        ErrorHandler.logError(error, { errorInfo });
      }

      render() {
        if (this.state.hasError) {
          if (errorFallback) {
            return errorFallback(this.state.error);
          }
          return React.createElement('div', null, 'Something went wrong.');
        }

        return React.createElement(WrappedComponent, this.props);
      }
    };
  };

export default ErrorHandler;
